import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiX, FiGrid, FiPlus, FiTrash2 } from 'react-icons/fi';
import { uploadBlogImage } from '../../services/api';

const PhotoGridModal = ({ isOpen, onClose, onInsert }) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [columns, setColumns] = useState(2);
  const [gap, setGap] = useState(8);
  const fileInputRef = useRef(null);

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      url: null,
    }));
    setImages(prev => [...prev, ...newImages]);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleInsert = async () => {
    if (images.length === 0) return;
    setUploading(true);
    try {
      const uploadedUrls = [];
      for (const img of images) {
        if (img.url) {
          uploadedUrls.push(img.url);
        } else {
          const fd = new FormData();
          fd.append('image', img.file);
          const res = await uploadBlogImage(fd);
          const url = res.data?.data?.url;
          if (url) uploadedUrls.push(url);
          else toast.warn('One image failed to upload');
        }
      }

      if (uploadedUrls.length > 0) {
        onInsert(uploadedUrls, columns, gap);
        images.forEach(img => URL.revokeObjectURL(img.preview));
        setImages([]);
        onClose();
        toast.success(`${uploadedUrls.length} photo${uploadedUrls.length > 1 ? 's' : ''} inserted as grid!`);
      }
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      images.forEach(img => URL.revokeObjectURL(img.preview));
      setImages([]);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <FiGrid className="w-5 h-5 text-[#243185]" />
            Insert Photo Grid
          </h3>
          <button onClick={handleClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Grid Settings */}
        <div className="p-5 border-b bg-gray-50">
          <div className="flex items-center gap-6">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Columns</label>
              <div className="flex gap-1.5">
                {[2, 3, 4].map(n => (
                  <button
                    key={n}
                    onClick={() => setColumns(n)}
                    className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
                      columns === n
                        ? 'bg-[#243185] text-white shadow-md'
                        : 'bg-white text-gray-600 border border-gray-300 hover:border-[#243185]'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1.5 block">Gap (px)</label>
              <input
                type="number"
                value={gap}
                onChange={e => setGap(Math.max(0, Math.min(24, parseInt(e.target.value) || 0)))}
                className="w-20 h-10 px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none"
                min="0"
                max="24"
              />
            </div>
          </div>
        </div>

        {/* Image Preview Grid */}
        <div className="p-5">
          {images.length > 0 && (
            <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
              {images.map((img, i) => (
                <div key={i} className="relative group rounded-lg overflow-hidden border border-gray-200">
                  <img src={img.preview} alt="" className="w-full h-32 object-cover" />
                  <button
                    onClick={() => removeImage(i)}
                    className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                  >
                    <FiTrash2 className="w-3 h-3" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[10px] px-2 py-0.5 truncate">
                    {img.file.name}
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full py-8 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#243185] hover:bg-[#243185]/5 transition-all flex flex-col items-center gap-2 disabled:opacity-50"
          >
            <FiPlus className="w-8 h-8 text-gray-400" />
            <span className="text-sm text-gray-500 font-medium">Add Photos</span>
            <span className="text-xs text-gray-400">Select multiple images at once</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={handleFilesSelected}
            className="hidden"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t bg-gray-50 rounded-b-2xl">
          <span className="text-sm text-gray-500">
            {images.length} photo{images.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={uploading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleInsert}
              disabled={images.length === 0 || uploading}
              className="px-5 py-2 bg-[#243185] text-white rounded-lg hover:bg-[#1a2460] transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <FiGrid className="w-4 h-4" />
                  Insert Grid
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoGridModal;
